import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { Box } from '@mui/system';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const HeaderContentTooltip = ({ header, content }) => {
  return (
    <Box>
      <Typography variant={'subtitle2'}>{header}</Typography>
      <Typography variant={'caption'}>{content}</Typography>
    </Box>
  );
};

// Map IdentifiedViaCategory enum values to tooltips and labels
const IDENTIFIED_VIA_TOOLTIPS = {
  BINARY_FINGERPRINT: {
    TITLE: 'Binary Fingerprint',
    CONTENT: 'Identifies components using binary-level analysis of compiled code structures.'
  },
  KERNEL_MODULE: {
    TITLE: 'Kernel Module',
    CONTENT: 'Detects Linux kernel modules (.ko files) through file analysis.'
  },
  CURATED_HASH: {
    TITLE: 'Curated Hash',
    CONTENT: 'Matches file hashes against our maintained database of known component files across distributions.'
  },
  LEGACY_HASH: {
    TITLE: 'Legacy Hash',
    CONTENT: 'Matches file hashes against a historical reference list of known files.'
  },
  LIBRARY_VERSION: {
    TITLE: 'Library + Version',
    CONTENT: 'Identifies shared libraries using both filename and version information extracted from the file.'
  },
  LIBRARY_NAME: {
    TITLE: 'Library Name',
    CONTENT: 'Identifies shared libraries using only the filename without version matching.'
  },
  PACKAGE_MANIFEST: {
    TITLE: 'Package Manifest',
    CONTENT: 'Extracts component information directly from package manager files (npm, pip, cargo, etc.).'
  },
  PE_METADATA: {
    TITLE: 'PE Metadata',
    CONTENT: 'Identifies Windows components using metadata embedded in Portable Executable files.'
  },
  SIGNATURE_DETECTION: {
    TITLE: 'Signature Detection',
    CONTENT: 'Identifies components using pattern matching against known software signatures.'
  },
  SYMBOL_INDEX: {
    TITLE: 'Symbol Index',
    CONTENT: 'Matches components based on exported function and symbol tables in binaries.'
  },
  USER_MODIFIED: {
    TITLE: 'User Modified',
    CONTENT: 'Identifies components that were manually remediated by the user.'
  },
  ROOT: {
    TITLE: 'Root',
    CONTENT: 'Identifies the root component of the asset.'
  }
};

const getIdentifiedViaInfo = category => {
  if (!category || !IDENTIFIED_VIA_TOOLTIPS[category]) {
    return null;
  }
  return {
    tooltip: IDENTIFIED_VIA_TOOLTIPS[category],
    label: IDENTIFIED_VIA_TOOLTIPS[category].TITLE
  };
};

export const NRIdentificationFormatter = ({ value }) => {
  const theme = useTheme();

  // Handle array of IdentifiedViaCategory enum values
  if (!value || !Array.isArray(value) || value.length === 0) {
    return null;
  }

  // Map each category to its tooltip info
  const identifiedViaItems = value
    .map(category => {
      const info = getIdentifiedViaInfo(category);
      return info ? { category, ...info } : null;
    })
    .filter(item => item !== null); // Only include items with valid tooltips

  if (identifiedViaItems.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: theme.spacing(0.5), flexWrap: 'wrap' }}>
      {identifiedViaItems.map((item, index) => {
        const badgeContent = (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              cursor: 'default',
              borderRadius: theme.spacing(0.5),
              border: `1px solid ${theme.palette.divider}`,
              alignItems: 'center',
              px: theme.spacing(1),
              py: theme.spacing(0.5),
              width: 'fit-content'
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }} color={'text.secondary'}>
              {item.label}
            </Typography>
          </Box>
        );

        return (
          <Tooltip key={index} title={<HeaderContentTooltip header={item.tooltip.TITLE} content={item.tooltip.CONTENT} />}>
            {badgeContent}
          </Tooltip>
        );
      })}
    </Box>
  );
};

export const NRIdentificationProvider = props => <DataTypeProvider formatterComponent={NRIdentificationFormatter} {...props} />;
